import { nanoid } from 'nanoid';

import type {
  CreateDigitalNavigationLogFormValues,
  DigitalNavigationLog,
  DigitalNavigationLogEntry,
  UpdateDigitalNavigationLogFormValues,
} from '@/store/digital-navigation-log/digital-navigation-log.dto';
import { convertVolume, round1, type VolumeUnit } from '@/utils/unitConversion';

import type { NavigationEntry, NavigationLogState } from './types';

const numToStr = (value: number | undefined): string =>
  value === undefined || value === null || Number.isNaN(value) ? '' : String(value);

const strToNum = (value: string): number => {
  if (!value || !value.trim()) return 0;
  const n = Number(value);
  return Number.isNaN(n) ? 0 : n;
};

const positionToCoords = (position: string): { latitude: string; longitude: string } => {
  const trimmed = position.trim();
  if (!trimmed) return { latitude: '', longitude: '' };
  const commaIdx = trimmed.indexOf(',');
  if (commaIdx === -1) return { latitude: trimmed, longitude: '' };
  return {
    latitude: trimmed.slice(0, commaIdx).trim(),
    longitude: trimmed.slice(commaIdx + 1).trim(),
  };
};

const coordsToPosition = (latitude: string, longitude: string): string => {
  if (latitude && longitude) return `${latitude}, ${longitude}`;
  return latitude || longitude || '';
};

const entryToDto = (entry: NavigationEntry): DigitalNavigationLogEntry => {
  const { latitude, longitude } = positionToCoords(entry.position);
  return {
    time: entry.time,
    latitude,
    longitude,
    course: entry.course,
    rpm: entry.rpm,
    personAtHelm: entry.personAtHelm,
    speed: entry.speed,
    weatherConditions: entry.weatherConditions,
    weatherVisibility: entry.visibility,
    windDirection: entry.windDir,
    windForce: entry.windForce,
    comments: entry.comments,
  };
};

const entryFromDto = (entry: DigitalNavigationLogEntry): NavigationEntry => ({
  id: nanoid(),
  time: entry.time,
  position: coordsToPosition(entry.latitude, entry.longitude),
  course: entry.course,
  rpm: entry.rpm,
  personAtHelm: entry.personAtHelm,
  speed: entry.speed,
  weatherConditions: entry.weatherConditions,
  visibility: entry.weatherVisibility,
  windDir: entry.windDirection,
  windForce: entry.windForce,
  comments: entry.comments,
});

/**
 * The service stores these figures exactly as keyed, in the log's own unit, so
 * a log keyed in the other unit is converted for display — and one keyed in
 * this unit comes back untouched.
 */
export const navigationLogToFormState = (
  log: DigitalNavigationLog,
  volumeUnit: VolumeUnit = 'gal',
): NavigationLogState => {
  const toDisplay = (value: number | undefined) => {
    const n = value ?? 0;
    return numToStr(
      log.volumeUnit === volumeUnit ? n : round1(convertVolume(n, log.volumeUnit, volumeUnit)),
    );
  };
  return {
    date: log.date,
    passageFrom: log.passageFrom,
    passageTo: log.passageTo,
    entries: log.entries.map(entryFromDto),
    maintenance: log.maintenanceReminders,
    fuelTakenOn: [toDisplay(log.fuelTakenOn1Gals), toDisplay(log.fuelTakenOn2Gals)],
    waterTakenOn: [toDisplay(log.waterTakenOn1Gals), toDisplay(log.waterTakenOn2Gals)],
  };
};

/** The figures go out as keyed, with their unit; the PDF export labels them with it. */
export const formStateToCreateValues = (
  state: NavigationLogState,
  volumeUnit: VolumeUnit = 'gal',
): CreateDigitalNavigationLogFormValues => ({
  date: state.date,
  passageFrom: state.passageFrom,
  passageTo: state.passageTo,
  entries: state.entries.map(entryToDto),
  maintenanceReminders: state.maintenance,
  fuelTakenOn1Gals: strToNum(state.fuelTakenOn[0]),
  fuelTakenOn2Gals: strToNum(state.fuelTakenOn[1]),
  waterTakenOn1Gals: strToNum(state.waterTakenOn[0]),
  waterTakenOn2Gals: strToNum(state.waterTakenOn[1]),
  volumeUnit,
});

export const formStateToUpdateValues = (
  id: string,
  state: NavigationLogState,
  volumeUnit: VolumeUnit = 'gal',
): UpdateDigitalNavigationLogFormValues => ({
  id,
  ...formStateToCreateValues(state, volumeUnit),
});
